import { queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { TopNav } from '../components/top-nav.js';
import {
  AdminForbiddenError,
  type AdminRecoveryQueueRow,
  type AdminTrustDropRow,
  fetchAdminTrustDrops,
  fetchAdminTrustRecoveryQueue,
} from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { meQuery } from '../lib/queries.js';

// Admin trust dashboard — /admin/trust.
//
// Operator surface for spec §0a.2 trust ladder ops. Two panes:
//   - recovery queue: pairs (brand, decision_type) with an outstanding
//     drop + their progress toward the 10-tick recovery threshold.
//     spot brands stuck cooling.
//   - recent drops: full archive of authority degradations across all
//     brands, newest first. spot reason patterns (founder reversals
//     vs budget exceeded vs founder flags).
//
// Gated server-side to MAGIG_ADMIN_EMAILS; the dashboard route also
// short-circuits non-admins to /home so they don't see a forbidden
// shell.

const CLEAN_STREAK_THRESHOLD = 10;

const dropsQuery = (days: number) =>
  queryOptions({
    queryKey: ['admin-trust-drops', days] as const,
    queryFn: () => fetchAdminTrustDrops(days),
    staleTime: 60_000,
    retry: false,
  });

const recoveryQueueQuery = () =>
  queryOptions({
    queryKey: ['admin-trust-recovery-queue'] as const,
    queryFn: fetchAdminTrustRecoveryQueue,
    staleTime: 60_000,
    retry: false,
  });

export const Route = createFileRoute('/admin/trust')({
  beforeLoad: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    // Try to pre-warm — if forbidden, the component renders the
    // gated state without throwing here.
    try {
      await Promise.all([
        queryClient.ensureQueryData(dropsQuery(30)),
        queryClient.ensureQueryData(recoveryQueueQuery()),
      ]);
    } catch {
      // forbidden / network errors handled in-component
    }
  },
  component: AdminTrustPage,
});

function AdminTrustPage() {
  useBrandTitle('admin', ['trust']);
  const [days, setDays] = useState(30);
  const drops = useQuery(dropsQuery(days));
  const queue = useQuery(recoveryQueueQuery());

  const forbidden =
    drops.error instanceof AdminForbiddenError || queue.error instanceof AdminForbiddenError;

  return (
    <div className="min-h-dvh bg-[var(--paper)]">
      <TopNav active={null} />
      <main className="mx-auto max-w-[1080px] px-4 py-8 sm:px-6 sm:py-10">
        <header className="mb-7">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            ↘ admin · trust
          </p>
          <h1 className="mt-1 font-display text-[28px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink)]">
            mag's authority drops + recovery.
          </h1>
          <p className="mt-2 text-[13px] text-[var(--ink)]/65">
            ops view of spec §0a.2 trust ladder. drops land when the founder flags a receipt as
            wrong, reverses an action within 24h, or mag's autonomous spend exceeds the cap.
            recovery fires on {CLEAN_STREAK_THRESHOLD} consecutive clean executions per (brand,
            decision_type).
          </p>
        </header>

        {forbidden ? (
          <Forbidden />
        ) : (
          <>
            <RecoveryQueueSection queue={queue.data ?? []} loading={queue.isLoading} />
            <RecentDropsSection
              drops={drops.data ?? []}
              loading={drops.isLoading}
              days={days}
              onDaysChange={setDays}
            />
          </>
        )}
      </main>
    </div>
  );
}

function Forbidden() {
  return (
    <div className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-6 shadow-[4px_4px_0_var(--pulse)]">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--pulse)]">
        ↘ forbidden
      </p>
      <h3 className="mt-1 font-display text-[16px] font-bold tracking-[-0.01em] text-[var(--ink)]">
        not for you.
      </h3>
      <p className="mt-2 text-[13px] text-[var(--ink)]/65">
        this surface is gated to <code className="font-mono">MAGIG_ADMIN_EMAILS</code>. your account
        isn't on the list.
      </p>
    </div>
  );
}

function RecoveryQueueSection({
  queue,
  loading,
}: {
  queue: AdminRecoveryQueueRow[];
  loading: boolean;
}) {
  const stillCooling = useMemo(() => queue.filter((r) => !r.alreadyRecovered), [queue]);
  const alreadyRecovered = useMemo(() => queue.filter((r) => r.alreadyRecovered), [queue]);
  return (
    <section className="mb-8 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
          ↘ recovery queue
        </p>
        <span className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
          {stillCooling.length} cooling · {alreadyRecovered.length} recovered
        </span>
      </div>
      {loading ? (
        <p className="mt-3 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
          reading…
        </p>
      ) : queue.length === 0 ? (
        <p className="mt-3 text-[13px] text-[var(--ink)]/65">
          no outstanding drops anywhere. mag is autonomous everywhere.
        </p>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {stillCooling.map((row) => (
            <QueueRow key={`${row.brandId}-${row.decisionType}`} row={row} />
          ))}
          {alreadyRecovered.length > 0 ? (
            <>
              <li className="mt-2 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/45">
                ↘ already recovered
              </li>
              {alreadyRecovered.map((row) => (
                <QueueRow key={`${row.brandId}-${row.decisionType}`} row={row} />
              ))}
            </>
          ) : null}
        </ul>
      )}
    </section>
  );
}

function QueueRow({ row }: { row: AdminRecoveryQueueRow }) {
  const progress = Math.min(1, row.cleanStreakCount / CLEAN_STREAK_THRESHOLD);
  return (
    <li className="rounded-xl border border-[var(--ink)]/10 bg-[var(--paper)] p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-display text-[14px] font-semibold text-[var(--ink)]">
          {row.brandName}
          <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--ink)]/55">
            {row.decisionType}
          </span>
        </p>
        <span
          className={`rounded-full px-2 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.08em] ${
            row.alreadyRecovered ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900'
          }`}
        >
          {row.alreadyRecovered ? 'recovered' : row.currentLevel}
        </span>
      </div>
      <p className="mt-1 text-[12px] text-[var(--ink)]/65">
        dropped {formatRelative(row.lastDropAt)} · reason: <code>{row.lastDropReason}</code>
      </p>
      {!row.alreadyRecovered ? (
        <div className="mt-2">
          <div className="h-1.5 w-full rounded-full bg-[var(--ink)]/10">
            <div
              className="h-1.5 rounded-full bg-[var(--plum)]"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            {row.cleanStreakCount} / {CLEAN_STREAK_THRESHOLD} clean runs
          </p>
        </div>
      ) : null}
    </li>
  );
}

function RecentDropsSection({
  drops,
  loading,
  days,
  onDaysChange,
}: {
  drops: AdminTrustDropRow[];
  loading: boolean;
  days: number;
  onDaysChange: (d: number) => void;
}) {
  return (
    <section className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
          ↘ recent drops
        </p>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onDaysChange(opt)}
              className={`rounded-full border-[1.5px] border-[var(--ink)] px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] transition ${
                days === opt
                  ? 'bg-[var(--ink)] text-[var(--spell)]'
                  : 'bg-white text-[var(--ink)] hover:bg-[var(--paper)]'
              }`}
            >
              {opt}d
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <p className="mt-3 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
          reading…
        </p>
      ) : drops.length === 0 ? (
        <p className="mt-3 text-[13px] text-[var(--ink)]/65">no drops in the past {days} days.</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-1">
          {drops.map((d) => (
            <li
              key={d.id}
              className="grid grid-cols-[120px_120px_1fr_auto] items-baseline gap-3 border-b border-[var(--ink)]/8 py-1.5 text-[12.5px] last:border-b-0"
            >
              <span className="truncate font-display text-[13px] font-semibold text-[var(--ink)]">
                {d.brandName}
              </span>
              <span className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-[var(--ink)]/65">
                {d.decisionType}
              </span>
              <span className="truncate text-[var(--ink)]/80" title={d.receipt ?? d.reason}>
                {d.receipt ?? `(${d.reason})`}
              </span>
              <span className="flex items-baseline gap-2">
                <code
                  className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.06em] ${reasonChip(d.reason)}`}
                >
                  {d.fromLevel} → {d.toLevel}
                </code>
                <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--ink)]/45">
                  {formatRelative(d.occurredAt)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function reasonChip(reason: string): string {
  switch (reason) {
    case 'founder_flagged_mistake':
      return 'bg-rose-50 text-rose-900';
    case 'founder_reversal_24h':
      return 'bg-amber-50 text-amber-900';
    case 'mistake_budget_exceeded':
      return 'bg-purple-50 text-purple-900';
    case 'clean_streak_recovery':
      return 'bg-emerald-50 text-emerald-900';
    default:
      return 'bg-zinc-100 text-zinc-700';
  }
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return iso;
  const sec = Math.max(1, Math.round(ms / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}
